import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { type AppInventoryState, AppInventoryVersion } from '@openx/types/targeting/appInventory.ts';
import type { TargetingProps } from '@openx/types/targeting/targeting.ts';
import type { TargetingAppInventory } from '@openx/types/targeting/targetingAppInventoryTypes.ts';
import { useMemo, useState } from 'react';
import { TargetingFieldPathProvider, useTargetingContext } from '../../utils/context/TargetingContext.tsx';
import { appInventoryApiToState } from '../shared/AppInventory/utils.ts';
import { TargetingDrawer } from '../shared/TargetingDrawer/TargetingDrawer.tsx';
import { AppInventoryReadOnly } from './AppInventoryReadOnly.tsx';
import { AppInventoryItems } from './components/AppInventoryItems/AppInventoryItems.tsx';
import { AppInventoryTopBox } from './components/AppInventoryTopBox/AppInventoryTopBox.tsx';
import { useAppInventoryData } from './hooks/useAppInventoryData.tsx';

const AppInventoryContent = ({
  targetingParams,
  appGroupsMap,
  ...props
}: TargetingProps<TargetingAppInventory> & { appGroupsMap?: Map<string, string> }) => {
  const { name, field, readonly, accountUid, displayContext } = props;

  const initState = useMemo(
    () => appInventoryApiToState(targetingParams, appGroupsMap),
    [targetingParams, appGroupsMap]
  );
  const [selectedInventory, setSelectedInventory] = useState<AppInventoryState | null>(null);

  const {
    userChangedInitialValue,
    inventoryState,
    dispatchInventory,
    inputInstanceValue,
    instanceState,
    dispatchInstanceState,
    isDataUpdated,
    appBundleIdDuplicateErrors,
    setAppBundleIdDuplicateErrors,
    inventoryListItems,
    setInventoryListItems,
    contentOption,
    setContentOption,
    appInventoryType,
    setAppInventoryType,
    appNameComparisonType,
    setAppNameComparisonType,
    bundleIdComparisonType,
    setBundleIdComparisonType,
    isOpen,
    onDrawerClose,
    onDrawerApply,
    onRemoveAll,
    onRemoveParameter,
    onRemoveAppName,
    onRemoveBundleIdManual,
    onRemoveBundleIdInventoryList,
    openDrawerWithPreselectedType,
  } = useAppInventoryData({
    initState: { ...initState, ...(selectedInventory ?? {}) },
    setSelectedInventory,
    ...props,
    inventoryVersion: AppInventoryVersion.APP_INVENTORY_ONLY_V2,
  });

  return (
    <TargetingFieldPathProvider value={field}>
      <AppInventoryReadOnly
        name={name}
        field={field}
        userChangedInitialValue={userChangedInitialValue}
        readonly={readonly}
        targetingParams={inventoryState}
        onOpenDrawerClick={openDrawerWithPreselectedType}
        onRemoveClick={onRemoveAll}
        onRemoveParameter={onRemoveParameter}
        dispatchInventory={dispatchInventory}
        setInventoryListItems={setInventoryListItems}
        inventoryListItems={inventoryListItems}
        onRemoveAppName={onRemoveAppName}
        onRemoveBundleIdManual={onRemoveBundleIdManual}
        onRemoveBundleIdInventoryList={onRemoveBundleIdInventoryList}
      />

      {isOpen && (
        <TargetingDrawer name={name} onClose={onDrawerClose} onApply={onDrawerApply} isApplyDisabled={!isDataUpdated}>
          <AppInventoryTopBox
            targetingParams={inventoryState}
            dispatchInventory={dispatchInventory}
            dispatchInstance={dispatchInstanceState}
            accountUid={accountUid}
            setAppBundleIdDuplicateErrors={setAppBundleIdDuplicateErrors}
            inventoryListItems={inventoryListItems}
            displayContext={displayContext}
            contentOption={contentOption}
            setContentOption={setContentOption}
            appInventoryType={appInventoryType}
            setAppInventoryType={setAppInventoryType}
            appNameComparisonType={appNameComparisonType}
            setAppNameComparisonType={setAppNameComparisonType}
            bundleIdComparisonType={bundleIdComparisonType}
            setBundleIdComparisonType={setBundleIdComparisonType}
          />

          <AppInventoryItems
            targetingParams={inventoryState}
            readonly={false}
            dispatchInventory={dispatchInventory}
            dispatchInstance={dispatchInstanceState}
            instanceState={instanceState}
            inputInstanceValue={inputInstanceValue}
            accountUid={accountUid}
            appBundleIdDuplicateErrors={appBundleIdDuplicateErrors}
            setInventoryListItems={setInventoryListItems}
            inventoryListItems={inventoryListItems}
            field={field}
            // App Inventory specific props
            appNameComparisonType={appNameComparisonType}
            setAppNameComparisonType={setAppNameComparisonType}
            bundleIdComparisonType={bundleIdComparisonType}
            setBundleIdComparisonType={setBundleIdComparisonType}
          />
        </TargetingDrawer>
      )}
    </TargetingFieldPathProvider>
  );
};

export const AppInventory = (props: TargetingProps<TargetingAppInventory>) => {
  const { useResolveAppGroupIds } = useTargetingContext();
  const { appGroupsMap, isLoading } = useResolveAppGroupIds?.(props.targetingParams?.appgroup?.val) ?? {
    appGroupsMap: new Map<string, string>(),
    isLoading: false,
  };

  if (isLoading) return <Loader />;

  return <AppInventoryContent {...props} appGroupsMap={appGroupsMap} />;
};
